import { normalizeProviderSurfaceKey } from "../utils/providerSurfaceRegistry";

const META_PARENT_KEY = "meta";
const META_CHILD_KEYS = new Set(["facebook", "instagram"]);

const META_PROVIDER_ALIASES = new Set([
  "meta",
  "meta_ads",
  "meta_ads_manager",
  "meta_business",
  "meta_business_suite",
]);

const PERFORMANCE_METRIC_ALIASES = Object.freeze({
  spend: [
    "clientFacingSpend",
    "client_facing_spend",
    "billedSpend",
    "billed_spend",
    "clientBilledSpend",
    "client_billed_spend",
    "reportSpend",
    "report_spend",
    "spend",
    "spend_usd",
  ],
  impressions: ["impressions", "reportImpressions", "report_impressions"],
  clicks: ["clicks", "reportClicks", "report_clicks"],
  engagements: ["engagements", "actions", "action_count", "actionCount"],
  videoViews: ["videoViews", "video_views", "views"],
  conversions: ["conversions", "conversion_count", "conversionCount"],
  conversionValue: [
    "conversionValue",
    "conversion_value",
    "conversionValueUsd",
    "conversion_value_usd",
  ],
});

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getPerformanceMetricValue = (channel, aliases) => {
  const sources = [
    channel,
    channel?.metrics,
    channel?.totals,
    channel?.aggregateTotals,
    channel?.aggregate_totals,
  ].filter(isPlainObject);

  for (const alias of aliases) {
    for (const source of sources) {
      const value = toFiniteNumber(source?.[alias]);
      if (value !== null) return value;
    }
  }

  return null;
};

const getPerformanceMetricVector = (channel = {}) =>
  Object.entries(PERFORMANCE_METRIC_ALIASES).reduce((metrics, [key, aliases]) => {
    metrics[key] = getPerformanceMetricValue(channel, aliases);
    return metrics;
  }, {});

const nearlyEqual = (left, right) => {
  const scale = Math.max(1, Math.abs(left), Math.abs(right));
  return Math.abs(left - right) <= scale * 1e-6;
};

export const canonicalizeSherlocPlatformKey = (rawKey) => {
  const providerKey = normalizeProviderSurfaceKey(rawKey);
  if (providerKey) return providerKey;
  const raw = String(rawKey ?? "").trim();
  if (!raw) return "";
  const compact = raw
    .toLowerCase()
    .replace(/[()]/g, " ")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const underscored = compact.replace(/\s+/g, "_");
  const explicitAliases = {
    meta_facebook: "facebook",
    meta_instagram: "instagram",
    google_youtube: "youtube",
    stackadapt_ctv: "ctv",
    stackadapt_display: "display",
    programmatic_display: "display",
  };
  if (explicitAliases[underscored]) return explicitAliases[underscored];
  if (META_PROVIDER_ALIASES.has(underscored)) return META_PARENT_KEY;

  const has = (word) => compact.includes(word);

  if (has("overall")) return "overall";
  if (compact === "fb" || compact === "meta fb" || has("meta facebook")) return "facebook";
  if (has("facebook")) return "facebook";
  if (compact === "ig" || compact === "meta ig" || has("meta instagram")) return "instagram";
  if (has("instagram")) return "instagram";
  if (has("audience network") || (has("audience") && has("network"))) return "audience_network";
  if (has("messenger")) return "messenger";
  if (has("tiktok") || has("tik tok")) return "tiktok";
  if (compact === "x" || has("twitter") || has("x com")) return "x";
  if (has("youtube") || has("you tube")) return "youtube";

  if (
    has("google analytics") ||
    has("google analytic") ||
    compact === "ga4" ||
    compact === "ga" ||
    has("website analytics") ||
    has("site analytics") ||
    has("web analytics")
  ) {
    return "google_analytics";
  }

  if (
    compact === "google" ||
    has("google search") ||
    has("paid search") ||
    has("ppc") ||
    (has("google") && has("search")) ||
    (has("google") && has("paid"))
  ) {
    return "google_search";
  }

  if (has("rumble")) return "rumble";
  if (has("truth social") || has("truthsocial") || (has("truth") && has("social"))) {
    return "truth_social";
  }
  if (has("dooh") || has("digital out of home") || has("digital out-of-home") || has("ooh")) {
    return "dooh";
  }
  if (has("ctv") || has("connected tv") || has("ott") || has("fast")) return "ctv";
  if (has("stackadapt") || has("programmatic")) return "programmatic";
  if (has("olv") || has("online video") || has("pre roll") || has("preroll") || has("video")) {
    return "video";
  }
  if (has("audio") || has("digital audio") || has("podcast") || has("spotify") || has("pandora")) {
    return "audio";
  }
  if (has("display") || has("banner")) return "display";
  if (has("mobile") || has("in app") || has("in-app")) return "mobile";
  if (has("in game") || has("ingame") || has("gaming")) return "ingame";
  if (has("email") || has("e mail") || has("crm")) return "email";
  if (has("cinema") || has("theatrical") || has("theater") || has("movie")) return "cinema";

  return underscored;
};

export const hasMeaningfulSherlocPerformanceMetrics = (channel = {}) =>
  Object.values(getPerformanceMetricVector(channel)).some(
    (value) => Number.isFinite(value) && value > 0
  );

const metaMetricsAreRepresentedByChildren = (metaChannel, childChannels) => {
  const metaMetrics = getPerformanceMetricVector(metaChannel);
  const meaningfulMetaMetrics = Object.entries(metaMetrics).filter(
    ([, value]) => Number.isFinite(value) && value > 0
  );
  if (!meaningfulMetaMetrics.length) return false;

  return meaningfulMetaMetrics.every(([metricKey, metaValue]) => {
    const childValues = childChannels
      .map((channel) => getPerformanceMetricVector(channel)[metricKey])
      .filter(Number.isFinite);
    if (!childValues.length) return false;
    const childTotal = childValues.reduce((total, value) => total + value, 0);
    return nearlyEqual(metaValue, childTotal);
  });
};

export const filterSherlocPerformancePlatformEntries = (entries = []) => {
  const sourceEntries = Array.isArray(entries) ? entries : [];
  const normalizedEntries = sourceEntries.map((entry) => ({
    entry,
    key: canonicalizeSherlocPlatformKey(entry?.[0]),
    channel: isPlainObject(entry?.[1]) ? entry[1] : {},
  }));
  const childChannels = normalizedEntries
    .filter(({ key }) => META_CHILD_KEYS.has(key))
    .map(({ channel }) => channel);
  const meaningfulChildChannels = childChannels.filter(hasMeaningfulSherlocPerformanceMetrics);

  return normalizedEntries
    .filter(({ key, channel }) => {
      if (key !== META_PARENT_KEY) return true;
      // Meta is the provider parent: keep legacy/distinct performance, not an empty or child-duplicated card.
      if (!hasMeaningfulSherlocPerformanceMetrics(channel)) return false;
      if (!childChannels.length || !meaningfulChildChannels.length) return true;
      return !metaMetricsAreRepresentedByChildren(channel, meaningfulChildChannels);
    })
    .map(({ entry }) => entry);
};
